"use client";

import { useEffect, useState } from "react";
import { LayoutWrapper } from "./components/layout-wrapper";
import { ResourceList } from "./components/resource-list";
import { EventBooking } from "./event/event-booking";
import { Booking } from "@/types/booking";
import { ServiceBooking } from "./service/service-booking";
import { WebinarBooking } from "./webinar/webinar-booking";
import { LayoutType, PageView } from "../types/enum";
import { ApiEndpoint, apiService } from "@/lib/api";
import { Resource } from "../types/resource";
import { ErrorComponent } from "./error";
import { BookingSuccess } from "./components/booking-success";
import { BookingCancel } from "./components/booking-cancel";
import { ResourceSidebar } from "./components/resource-sidebar";
import { usePixelTracker } from "../hooks/pixel-tracker";

interface MainComponentProps {
  page: any;
  resource?: Resource | null;
  view: PageView;
  id?: string;
  onNavigate?: (path: string) => void;
  queryParams?: Record<string, string>;
}

export default function MainComponent({
  page,
  resource: initialResource = null,
  view: initialView = "resources",
  id,
  onNavigate,
  queryParams,
}: MainComponentProps) {
  const tracker = usePixelTracker();

  const [currentView, setCurrentView] = useState<PageView>(initialView);
  const [selectedResource, setSelectedResource] =
    useState<any>(initialResource);

  const [layout, setLayout] = useState<LayoutType>("default");
  const [booking, setBooking] = useState<Booking>({} as Booking);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<{
    message: string;
    details: string;
  } | null>(null);

  useEffect(() => {
    setCurrentView(initialView);
  }, [initialView]);

  useEffect(() => {
    if (
      ["booking", "cancel", "reschedule"].includes(initialView) &&
      !booking?.booking_id
    ) {
      fetchBookingById();
    } else {
      setIsLoading(false);
    }
  }, [initialView]);

  const fetchBookingById = async () => {
    try {
      const data: any = await apiService.get(ApiEndpoint.bookings, id!);
      if (!data) {
        throw new Error("No booking found");
      }
      setBooking(data as unknown as Booking);
      const r = page.resources.find(
        (x: Resource) => x.resource_id === data.resource?.resource_id
      );
      if (r) {
        setSelectedResource(r);
      } else {
        setSelectedResource(data.resource);
      }
    } catch (err: any) {
      setError({
        message: err.message || "No booking found",
        details:
          "This booking has been archived or deleted by the owner, or invalid id!",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResourceSelect = (resource: any) => {
    if (import.meta.env.MODE === "widget") {
      setSelectedResource(resource);
      setCurrentView(resource.type as PageView);
    } else {
      onNavigate?.(resource.slug);
    }

    // Meta, GA4 tracking
    tracker("resource_selected", {
      label: resource.name,
      resource_id: resource.resource_id,
    });
  };

  const handleDone = () => {
    setBooking({} as Booking);
    if (import.meta.env.MODE === "widget") {
      setSelectedResource(null);
      setCurrentView("resources");
    } else {
      onNavigate?.("/");
    }
  };

  const handleLayoutChange = (newLayout: LayoutType) => {
    setLayout(newLayout as LayoutType);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && error.message) {
    return (
      <ErrorComponent
        message={error.message}
        details={error.details}
      ></ErrorComponent>
    );
  }

  if (!selectedResource && currentView !== "resources") {
    return (
      <ErrorComponent
        message={"No resource found"}
        details={"This resources has been deleted or not available anymore"}
      ></ErrorComponent>
    );
  }

  return (
    <LayoutWrapper
      onLayoutChange={handleLayoutChange}
      layout={layout}
      removeBranding={page.remove_branding}
    >
      {currentView === "resources" && (
        <ResourceList page={page} onSelect={handleResourceSelect} />
      )}

      {currentView === "event" && selectedResource.type === "event" && (
        <EventBooking
          page={page}
          resource={selectedResource}
          onDone={handleDone}
          booking={booking}
          layout={layout}
          queryParams={queryParams}
        />
      )}

      {currentView === "webinar" && selectedResource.type === "webinar" && (
        <WebinarBooking
          page={page}
          resource={selectedResource}
          onDone={handleDone}
        />
      )}

      {currentView === "service" && selectedResource.type === "service" && (
        <ServiceBooking
          page={page}
          resource={selectedResource}
          onDone={handleDone}
        />
      )}

      {currentView === "booking" && booking && (
        <div className="flex flex-col lg:grid lg:grid-cols-[300px_1fr] h-full divide-y lg:divide-y-0 lg:divide-x">
          <ResourceSidebar
            page={page}
            resource={selectedResource}
            onBack={handleDone}
            booking={booking}
            currentView={currentView}
          />
          <BookingSuccess
            resource={selectedResource}
            booking={booking}
            onRefresh={fetchBookingById}
            autoRedirect={false}
          />
        </div>
      )}

      {currentView === "cancel" && booking && (
        <div className="flex flex-col lg:grid lg:grid-cols-[300px_1fr] h-full divide-y lg:divide-y-0 lg:divide-x">
          <ResourceSidebar
            page={page}
            resource={selectedResource}
            onBack={handleDone}
            booking={booking}
            currentView={currentView}
          />
          <BookingCancel
            resource={selectedResource}
            booking={booking}
            onBack={handleDone}
          />
        </div>
      )}

      {currentView === "reschedule" && booking && (
        <EventBooking
          page={page}
          resource={selectedResource}
          onDone={handleDone}
          booking={booking}
          layout={layout}
          queryParams={queryParams}
          currentView={currentView}
        />
      )}
    </LayoutWrapper>
  );
}
